import type { RegistroDeAuditoria } from '@/lib/supabase/tipos-bd'
import { fechaHora } from '@/shared/utilidades/formato'

import { ACCIONES, frase } from './frases'
import { Avatar, nombreDelRol } from './Persona'
import estilos from './PaginaAuditoria.module.css'

/**
 * POR PERSONA: cuánto hizo cada uno en el periodo y qué fue lo último.
 *
 * Es la otra pregunta de una auditoría: no «qué pasó el martes» sino «qué ha
 * hecho Jerson». Una tarjeta por persona, ordenadas por cuántas acciones
 * tienen, con el conteo por tipo de acción y su último movimiento.
 *
 * Se agrupa por PERSONA, no por persona y rol: si a alguien le cambiaron el
 * rol en el periodo, sigue siendo una sola tarjeta, con los roles que tuvo.
 */
export function PorPersona({ registros }: { registros: RegistroDeAuditoria[] }) {
  const grupos = new Map<string, RegistroDeAuditoria[]>()
  for (const r of registros) {
    const llave = r.actor_id ?? r.actor_nombre
    grupos.set(llave, [...(grupos.get(llave) ?? []), r])
  }
  const personas = [...grupos.values()].sort((a, b) => b.length - a.length)

  return (
    <div className={estilos.personas}>
      {personas.map((lista) => {
        const ultimo = lista[0] // vienen de la más nueva a la más vieja
        const { verbo, documento } = frase(ultimo)
        return (
          <article key={ultimo.actor_id ?? ultimo.actor_nombre} className={estilos.persona}>
            <header className={estilos.personaTope}>
              <Avatar nombre={ultimo.actor_nombre} rol={ultimo.actor_rol} />
              <div>
                <p className={estilos.personaNombre}>{ultimo.actor_nombre}</p>
                <p className={estilos.personaRol}>
                  {[...new Set(lista.map((r) => nombreDelRol(r.actor_rol)))].join(' · ')}
                </p>
              </div>
              <span className={estilos.personaTotal}>
                {lista.length}
                <small>{lista.length === 1 ? 'acción' : 'acciones'}</small>
              </span>
            </header>
            <ul className={estilos.conteos}>
              {ACCIONES.map(([tipo, texto]) => {
                const n = lista.filter((r) => r.tipo === tipo).length
                return n ? (
                  <li key={tipo} className={estilos[`tipo_${tipo}`]}>
                    {texto} <b>{n}</b>
                  </li>
                ) : null
              })}
            </ul>
            <p className={estilos.ultimo}>
              Lo último: {ultimo.tipo === 'nota' ? 'anotó en' : verbo} {documento}{' '}
              <b>{ultimo.documento_numero ?? ''}</b> · {fechaHora(ultimo.ocurrido_en)}
            </p>
          </article>
        )
      })}
    </div>
  )
}
